import { auth, db } from "../components/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const MIN_WAIT_MS = 75_000;

function genCode(uid, questId) {
  const base = (uid.slice(0, 4) + String(questId).slice(0, 4)).toUpperCase();
  const salt = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `CRM-${base}-${salt}`;
}

function lsKey(uid, qid) {
  return `quest:${uid}:${qid}:startedAt`;
}

/** Start: marks progress; optionally emits a code */
export async function issueCode(questId, withCode = true) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not logged in");

  const qid = String(questId);
  const progressRef = doc(db, "users", uid, "questProgress", qid);
  const snap = await getDoc(progressRef);

  let code = snap.exists() ? snap.data()?.code : undefined;
  if (withCode && !code) code = genCode(uid, qid);

  // если уже done — просто вернём существующий код и не будем трогать startedAt
  const isAlreadyDone = snap.exists() && snap.data()?.status === "done";

  await setDoc(
    progressRef,
    {
      questId: qid,
      status: isAlreadyDone ? "done" : "started",
      ...(withCode && code ? { code } : {}),
      ...(isAlreadyDone ? { doneAt: snap.data()?.doneAt || serverTimestamp() } : { startedAt: serverTimestamp(), startedAtMsClient: Date.now() }),
    },
    { merge: true }
  );

  // локальная отметка старта (для таймера и UI)
  try {
    if (!isAlreadyDone) {
      localStorage.setItem(lsKey(uid, qid), String(Date.now()));
    }
  } catch {}

  return { code: withCode ? code : "" };
}

export async function checkQuest(questId, proof = {}) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not logged in");

  const qid = String(questId);
  const progressRef = doc(db, "users", uid, "questProgress", qid);
  let progressSnap = await getDoc(progressRef);

  if (!progressSnap.exists()) {
    // мостик: если старт уже жали, но док ещё не прилетел — создадим его из localStorage
    let startedLocal = 0;
    try {
      startedLocal = Number(localStorage.getItem(lsKey(uid, qid)) || 0);
    } catch {}
    if (startedLocal > 0) {
      await setDoc(
        progressRef,
        {
          questId: qid,
          status: "started",
          startedAtMsClient: startedLocal,
          createdFromLocalMarker: true,
        },
        { merge: true }
      );
      progressSnap = await getDoc(progressRef);
    } else {
      throw new Error("First, press Start");
    }
  }

  const p = progressSnap.data() || {};
  let startedMs = 0;
  if (p.startedAt?.toMillis) startedMs = p.startedAt.toMillis();
  else if (typeof p.startedAtMsClient === "number") startedMs = p.startedAtMsClient;

  // если уже были в статусе done — идемпотентный возврат (ничего не начисляем)
  const eventId = `promo_${qid}`;
  const eventRef = doc(db, "users", uid, "milesEvents", eventId);
  const eventSnap = await getDoc(eventRef);
  if (eventSnap.exists() || p.status === "done") {
    // приведение прогресса к done (если почему-то не done)
    if (p.status !== "done") {
      await setDoc(
        progressRef,
        {
          questId: qid,
          status: "done",
          doneAt: serverTimestamp(),
          ...(p.code ? { code: p.code } : {}),
        },
        { merge: true }
      );
    }
    try {
      localStorage.removeItem(lsKey(uid, qid));
    } catch {}
    return { ok: true, already: true, message: "Reward already credited" };
  }

  if (!startedMs) {
    throw new Error("I don't see the task start. Click Start and try again later.");
  }

  const elapsed = Date.now() - startedMs;
  if (elapsed < MIN_WAIT_MS) {
    const waitLeft = Math.max(0, Math.ceil((MIN_WAIT_MS - elapsed) / 1000));
    throw new Error(`Checking... Try again later (≈ ${waitLeft} sec).`);
  }

  await setDoc(
    progressRef,
    {
      questId: qid,
      status: "done",
      doneAt: serverTimestamp(),
      proof: proof || {},
      ...(p.code ? { code: p.code } : {}),
    },
    { merge: true }
  );

  await setDoc(eventRef, {
    points: 15,
    source: "promo_task",
    status: "confirmed",
    meta: { questId: qid, proof: proof || {} },
    createdAt: serverTimestamp(),
  });

  try {
    localStorage.removeItem(lsKey(uid, qid));
  } catch {}

  return { ok: true, message: "+15 CrM credited" };
}
